import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import StreamViewer from "../components/StreamViewer";

/**
 * KidsLive — kid-safe wrapper around StreamViewer for live streams.
 *
 * Any time a kid taps a live stream from /kids/* routes they end up here
 * rather than the main Watch.jsx, so they never see grown-up chat
 * moderation tools, ads, or the main-site chrome.
 */
export default function KidsLive() {
  const { videoId } = useParams();
  const nav = useNavigate();
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/live/${videoId}`)
      .then((r) => {
        const l = r.data;
        if (!(l?.is_kids_safe || l?.category === "kids" || l?.brand === "tap-in-kids")) {
          toast.error("That stream isn't kid-safe");
          nav("/kids", { replace: true });
          return;
        }
        setLive(l);
      })
      .catch(() => {
        toast.error("Stream not found");
        nav("/kids", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [videoId, nav]);

  if (loading) return <div className="pt-32 text-white/60 text-center">Loading…</div>;
  if (!live) return null;

  return (
    <div data-testid="kids-live-page" className="pb-20 px-3 sm:px-6 md:px-10 max-w-5xl mx-auto text-white">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <Link data-testid="klive-back" to="/kids" className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to Kids
        </Link>
        <img src="/kids-learn-play-logo.png" alt="" className="w-24 h-auto drop-shadow-xl" draggable={false} />
      </div>

      <div className="rounded-2xl overflow-hidden ring-4 ring-red-500/40 shadow-2xl">
        <StreamViewer streamId={live.id} kidsBrand={true} />
      </div>

      <div className="mt-4">
        <div className="inline-flex items-center gap-2 bg-red-600 text-white font-black text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full animate-pulse">
          <span className="w-2 h-2 rounded-full bg-white" /> LIVE
        </div>
        <h1 data-testid="klive-title" className="font-display font-black text-2xl md:text-3xl tracking-tight mt-1">{live.title}</h1>
        {live.creator_name && (
          <Link to={`/kids/timeline/${live.creator_id}`} className="inline-flex items-center gap-2 mt-2 text-white/80 hover:text-white text-sm">
            <span className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-400 to-fuchsia-500 flex items-center justify-center text-sm">
              {live.creator_avatar || "⭐"}
            </span>
            @{live.handle || live.creator_name}
          </Link>
        )}
      </div>
    </div>
  );
}
